import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Users, 
  UserPlus, 
  Clock, 
  FileSpreadsheet, 
  AlertTriangle,
  RefreshCw,
  Calendar,
  CheckCircle2,
  XCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusCard = ({ title, value, icon: Icon, status, subtitle, borderColor }) => (
  <div 
    className={`bg-white border-l-4 ${borderColor || 'border-l-forest'} shadow-sm p-4 sm:p-6 rounded-r-lg hover:shadow-md transition-all duration-200`}
    data-testid={`status-card-${title.toLowerCase().replace(/\s/g, '-')}`}
  >
    <div className="flex items-start justify-between">
      <div className="min-w-0 flex-1">
        <p className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-1 sm:mb-2">{title}</p>
        <p className="text-xl sm:text-3xl font-heading font-bold text-stone-900 truncate">{value}</p>
        {subtitle && (
          <p className="text-xs sm:text-sm text-stone-500 mt-1 truncate">{subtitle}</p>
        )}
      </div>
      <div className={`p-2 sm:p-3 rounded-lg shrink-0 ml-2 ${status === 'success' ? 'bg-signal-success/10' : status === 'warning' ? 'bg-signal-warning/10' : status === 'error' ? 'bg-signal-error/10' : 'bg-forest/10'}`}>
        <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${status === 'success' ? 'text-signal-success' : status === 'warning' ? 'text-signal-warning' : status === 'error' ? 'text-signal-error' : 'text-forest'}`} />
      </div>
    </div>
    {status && (
      <div className="mt-3 sm:mt-4 flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${status === 'success' ? 'bg-signal-success' : status === 'warning' ? 'bg-signal-warning animate-pulse-slow' : status === 'error' ? 'bg-signal-error' : 'bg-forest'}`} />
        <span className="text-xs text-stone-500 capitalize">{status}</span>
      </div>
    )}
  </div>
);

const ActivityLogItem = ({ log }) => {
  const statusColors = {
    info: 'text-signal-info',
    success: 'text-signal-success',
    warning: 'text-signal-warning',
    error: 'text-signal-error'
  };

  const statusIcons = {
    info: Clock,
    success: CheckCircle2,
    warning: AlertTriangle,
    error: XCircle
  };

  const Icon = statusIcons[log.status] || Clock;

  return (
    <div className="activity-row border-b border-stone-200 py-3 last:border-0 px-2 flex items-start gap-2 sm:gap-3">
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${statusColors[log.status] || 'text-stone-400'}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-stone-800">{log.action}</p>
        <p className="text-xs text-stone-500 truncate">{log.details}</p>
      </div>
      <time className="text-xs text-stone-400 font-mono whitespace-nowrap shrink-0 hidden sm:block">
        {formatTime(log.timestamp)}
      </time>
    </div>
  );
};

const formatTime = (isoString) => {
  if (!isoString) return '-';
  try {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', { 
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return isoString;
  }
};

export const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [blastStatus, setBlastStatus] = useState(null);
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [showCalendar, setShowCalendar] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, logsRes, blastRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/activity-logs?limit=20`),
        axios.get(`${API}/blast/status`)
      ]);
      setStats(statsRes.data);
      setLogs(logsRes.data);
      setBlastStatus(blastRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const payload = {};
      if (dateRange.from) {
        payload.start_date = format(dateRange.from, 'yyyy-MM-dd');
      }
      if (dateRange.to) {
        payload.end_date = format(dateRange.to, 'yyyy-MM-dd');
      }
      
      const response = await axios.post(`${API}/sync/manual`, payload);
      if (response.data.success) {
        toast.success('Sync started successfully');
        setTimeout(fetchData, 2000);
      }
    } catch (error) {
      toast.error('Failed to start sync');
    } finally {
      setSyncing(false);
      setShowCalendar(false);
    }
  };

  const handleSheetsPush = async () => {
    setPushing(true);
    try {
      const response = await axios.post(`${API}/sync/sheets-push`);
      if (response.data.success) {
        toast.success(response.data.message);
        setTimeout(fetchData, 2000);
      } else {
        toast.error(response.data.message || 'Sheets push failed');
      }
    } catch (error) {
      toast.error('Failed to push to Google Sheets');
    } finally {
      setPushing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-forest animate-spin" />
      </div>
    );
  }

  const lastSyncStatus = stats?.last_sync?.status || 'none';
  const baselineWarning = !stats?.baseline_imported;

  return (
    <div className="space-y-4 sm:space-y-6 md:space-y-8 fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-stone-900 tracking-tight">Dashboard</h1>
          <p className="text-sm sm:text-base text-stone-500 mt-1">Monitor and manage camper enrollee syncs</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Popover open={showCalendar} onOpenChange={setShowCalendar}>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                className="gap-2 text-xs sm:text-sm"
                data-testid="date-range-btn"
              >
                <Calendar className="w-4 h-4" />
                <span className="hidden sm:inline">
                  {dateRange.from ? (
                    dateRange.to ? (
                      `${format(dateRange.from, 'MMM d')} - ${format(dateRange.to, 'MMM d')}`
                    ) : format(dateRange.from, 'MMM d, yyyy')
                  ) : 'Select Date Range'}
                </span>
                <span className="sm:hidden">
                  {dateRange.from ? format(dateRange.from, 'M/d') : 'Dates'}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <CalendarComponent
                mode="range"
                selected={dateRange}
                onSelect={setDateRange}
                numberOfMonths={1}
                data-testid="sync-calendar"
              />
              <div className="p-3 border-t flex justify-end gap-2">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setDateRange({ from: null, to: null })}
                >
                  Clear
                </Button>
              </div>
            </PopoverContent>
          </Popover>

          <Button 
            onClick={handleSheetsPush}
            disabled={pushing}
            className="bg-signal-info hover:bg-signal-info/90 text-white font-bold py-2 px-3 sm:px-6 rounded-md shadow-lg transition-all flex items-center gap-2 text-xs sm:text-sm uppercase tracking-wider"
            data-testid="sheets-push-btn"
          >
            <FileSpreadsheet className={`w-4 h-4 ${pushing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{pushing ? 'Pushing...' : 'Push to Sheets'}</span>
            <span className="sm:hidden">{pushing ? '...' : 'Sheets'}</span>
          </Button>

          <Button 
            onClick={handleSync}
            disabled={syncing}
            className="bg-forest hover:bg-forest-dark text-white font-bold py-2 px-3 sm:px-6 rounded-md shadow-lg transition-all flex items-center gap-2 text-xs sm:text-sm uppercase tracking-wider"
            data-testid="run-sync-btn"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{syncing ? 'Syncing...' : 'Run Sync Now'}</span>
            <span className="sm:hidden">{syncing ? '...' : 'Sync'}</span>
          </Button>
        </div>
      </div>

      {/* Baseline Warning */}
      {baselineWarning && (
        <Card className="border-earth bg-earth/5" data-testid="baseline-warning">
          <CardContent className="py-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-earth shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-stone-800">Baseline Not Imported</p>
              <p className="text-sm text-stone-600">Upload your initial baseline list to enable duplicate protection.</p>
            </div>
            <Button variant="outline" className="w-full sm:w-auto" asChild>
              <a href="/import">Import Now</a>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Initial Blast Complete */}
      {blastStatus && blastStatus.blast_completed && (
        <Card className="border-signal-success bg-signal-success/5">
          <CardContent className="py-4 flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-signal-success shrink-0" />
            <div>
              <p className="font-medium text-stone-800">Initial Emails Sent</p>
              <p className="text-sm text-stone-600">{blastStatus.emails_sent} parent emails delivered. New enrollees are emailed automatically.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
        <StatusCard
          title="Total Campers"
          value={stats?.total_campers || 0}
          icon={Users}
          status="success"
          subtitle={`${stats?.baseline_count || 0} from baseline`}
          borderColor="border-l-forest"
        />
        <StatusCard
          title="New Today"
          value={stats?.new_enrollees_today || 0}
          icon={UserPlus}
          status={stats?.new_enrollees_today > 0 ? 'success' : 'info'}
          subtitle="Detected in last sync"
          borderColor="border-l-signal-success"
        />
        <StatusCard
          title="Last Sync"
          value={stats?.last_sync ? formatTime(stats.last_sync.started_at) : 'Never'}
          icon={Clock}
          status={lastSyncStatus === 'success' ? 'success' : lastSyncStatus === 'failed' ? 'error' : 'warning'}
          subtitle={stats?.last_sync ? `${stats.last_sync.new_records_found} new found` : 'No syncs yet'}
          borderColor={lastSyncStatus === 'success' ? 'border-l-signal-success' : lastSyncStatus === 'failed' ? 'border-l-signal-error' : 'border-l-signal-warning'}
        />
        <StatusCard
          title="Last Sheets Push"
          value={stats?.last_sheets_push ? formatTime(stats.last_sheets_push) : 'Never'}
          icon={FileSpreadsheet}
          status={stats?.last_sheets_push ? 'success' : 'warning'}
          borderColor="border-l-signal-info"
        />
      </div>

      {/* Activity Log */}
      <Card>
        <CardHeader className="pb-3 px-4 sm:px-6">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg sm:text-xl font-heading">Recent Activity</CardTitle>
            <Badge variant="outline" className="font-mono text-xs">
              {logs.length} events
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="px-4 sm:px-6">
          <div 
            className="font-mono text-sm bg-stone-50 border border-stone-200 rounded-lg max-h-[300px] sm:max-h-[400px] overflow-y-auto scrollbar-thin"
            data-testid="activity-log"
          >
            {logs.length > 0 ? (
              logs.map((log, idx) => (
                <ActivityLogItem key={log.id || idx} log={log} />
              ))
            ) : (
              <div className="p-8 text-center text-stone-500">
                <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No activity yet</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
